'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@achra/ui/collapsible';
import { cn } from '@achra/ui/lib/utils';
import { Maximize2, Minimize2 } from 'lucide-react';
import Image from 'next/image';
import { useHomepageBanner } from '@/modules/networks/hooks/use-homepage-banner';
import { ConnectLink } from '@/modules/shared/components/connect-link';
interface HomepageBannerProps {
  backgroundImage: string;
  title: string;
  description: string;
  isLoggedIn?: boolean;
  className?: string;
}
function HomepageBanner(t0) {
  const $ = _c(34);
  const {
    backgroundImage,
    title,
    description,
    isLoggedIn: t1,
    className
  } = t0;
  const isLoggedIn = t1 === undefined ? false : t1;
  const {
    isExpanded,
    collapsibleElement,
    handleIsExpanded,
    showWhitelistOverlay
  } = useHomepageBanner();
  const t2 = !isExpanded && "min-h-21.5! py-7.75! pl-6 sm:py-7! lg:pl-8 xl:py-6!";
  const t3 = isLoggedIn && "pb-31 sm:pb-21.5 md:pb-5.75 lg:pb-11.75";
  let t4;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("text-primary-foreground relative flex w-full flex-col overflow-hidden rounded-xl px-6 py-8 transition-all duration-300 ease-out sm:pt-5.5 md:px-6 md:pt-8 lg:px-8 lg:pt-8 xl:p-8", t2, t3, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      objectFit: "cover"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== backgroundImage) {
    t6 = <Image src={backgroundImage} alt="Banner background" fill={true} priority={true} fetchPriority="high" quality={75} sizes="100vw" style={t5} className="absolute z-0" />;
    $[5] = backgroundImage;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isExpanded || $[8] !== showWhitelistOverlay) {
    t7 = !showWhitelistOverlay && <CollapsibleTrigger asChild={true} className="absolute top-3 right-3 z-1"><Button className="hover:bg-secondary-foreground/30 h-6 w-6 bg-transparent shadow-none [&:has(>svg)]:p-1" aria-label="Toggle homepage banner">{isExpanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}</Button></CollapsibleTrigger>;
    $[7] = isExpanded;
    $[8] = showWhitelistOverlay;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = !isExpanded && !isLoggedIn && "pb-0";
  const t9 = !isExpanded && isLoggedIn && "hidden sm:block";
  let t10;
  if ($[10] !== t8 || $[11] !== t9) {
    t10 = cn("z-1 pb-4 text-xl/6 font-bold transition-all duration-300 ease-out sm:text-2xl sm:leading-[120%] xl:text-3xl", t8, t9);
    $[10] = t8;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== title) {
    t11 = <span className={t10}>{title}</span>;
    $[13] = t10;
    $[14] = title;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = isLoggedIn && "max-w-131 md:max-w-99.75 lg:max-w-161.75";
  let t13;
  if ($[16] !== t12) {
    t13 = cn("data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down z-1 max-w-161.75 overflow-hidden text-sm leading-5.5 xl:max-w-168.75 xl:text-base xl:leading-normal", t12);
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== collapsibleElement || $[19] !== description || $[20] !== t13) {
    t14 = <CollapsibleContent ref={collapsibleElement} className={t13}>{description}</CollapsibleContent>;
    $[18] = collapsibleElement;
    $[19] = description;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== isExpanded || $[23] !== isLoggedIn) {
    t15 = isLoggedIn && <div className={cn("absolute right-6 bottom-4 z-1 w-[calc(100%-48px)] transition-all duration-300 ease-out sm:right-4 sm:w-fit sm:max-w-73.75 md:right-6 md:bottom-6 lg:right-6 lg:bottom-6", !isExpanded && "right-11 bottom-4! w-[calc(100%-88px)] sm:right-10! md:right-15!")}><ConnectLink disabled={true} href="https://connect.achra.network" action="edit" driveName="Sky Network Admin" variant="primary" /></div>;
    $[22] = isExpanded;
    $[23] = isLoggedIn;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== handleIsExpanded || $[26] !== isExpanded || $[27] !== t11 || $[28] !== t14 || $[29] !== t15 || $[30] !== t4 || $[31] !== t6 || $[32] !== t7) {
    t16 = <Collapsible open={isExpanded} onOpenChange={handleIsExpanded} className={t4}>{t6}{t7}{t11}{t14}{t15}</Collapsible>;
    $[25] = handleIsExpanded;
    $[26] = isExpanded;
    $[27] = t11;
    $[28] = t14;
    $[29] = t15;
    $[30] = t4;
    $[31] = t6;
    $[32] = t7;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
}
export { HomepageBanner };